/**
 * Die Bestellungen kommen aus JTL, die Arbeitsdaten aus dem Hub.
 *
 * ============================ WARUM ES DIESE DATEI GIBT ============================
 *
 * Bis zum 2026-08-15 speiste sich das Board aus der tool-eigenen `orders`-Tabelle. Die
 * Anlagemaske schrieb dorthin aber nur den Lieferanten - Lager, Liefertermin, Rechnungsnummer und
 * saemtliche Positionen fielen weg (siehe `ANLAGEMASKE_VERWORFENE_FELDER`). Im Board stand
 * anschliessend eine Zeile ohne Inhalt: "Ohne Bestellnummer", kein Lieferant, kein Datum.
 *
 * Entscheidung des Auftraggebers: das Tool speichert nichts, was JTL bereits kennt. Alles, was JTL
 * fuehrt, wird bei jedem Seitenaufbau von dort gelesen; im Hub bleibt ausschliesslich, was JTL
 * NICHT kennt.
 *
 *   aus JTL   Lieferant, Waehrung, Bestellnummer, Liefer- und Bestelldatum, Status, Bestaetigung,
 *             Fremdbelegnummer, kLager/lagerName (JTL-Warenlager, siehe "WELCHE SEITE GEWINNT"
 *             unten), Bezugsauftrag, interner Kommentar, Liefertermin-Freitext (`cDruckAnmerkung`,
 *             seit Auftrag "Liefertermin auf echtes JTL-Feld umstellen", 2026-08-26), Waehrungsfaktor,
 *             bRowversion (fuer optimistisches Sperren beim Schreiben, siehe `jtlBestellkopfAendern`
 *             unten), alle Positionen mit Menge, EK, Steuersatz, geliefert und offen, Sortierung,
 *             Lieferanten-Bezeichnung und Lieferanten-Artikelnummer
 *   aus Hub   Board-Status (Constraint 18 haelt ihn ausdruecklich von JTLs nStatus getrennt),
 *             Zahlungsplan, Dokumente, Aufgaben, Sendungen, Kurssicherung, Kosten
 *
 * Ziellager und interner Kommentar (`Drawer/MainTab/Stammdaten.tsx`/`BestellungTab.tsx`) werden seit
 * Auftrag C2b (2026-08-22) NUR NOCH ueber JTL gelesen UND geschrieben (`jtlBestellkopfAendern`
 * unten, `PATCH /api/jtl/orders/:k`) - `order_extras.warehouse`/`.notes` werden von diesen beiden
 * Komponenten nicht mehr angefasst. RICHTIGGESTELLT (Auftrag E): die Spalten selbst sind seither
 * ENTFERNT (Auftrag D, 2026-08-22, `backend/migrations/017_order_extras_warehouse_notes_entfernen.sql`)
 * - kein `Order`-Objekt traegt seither je einen `warehouse`-Wert. `lib/lager.ts#lagerAuswahl` liest
 * `bestellung.warehouse` zwar unveraendert (siehe dortiger Kopfkommentar), findet dort seit der
 * Migration aber fuer KEINEN Aufrufer mehr etwas - auch nicht fuer die ANLAGEMASKE, die dieselbe
 * (extras-lose) `bestellungen`-Liste durchreicht.
 *
 * Der Umfang traegt das: 94 offene Bestellungen mit 1003 Positionen, 24-29 ms je Abfrage (gemessen
 * am 2026-08-15). Ein Zwischenspeicher waere die teurere Loesung - er braeuchte eine
 * Ungueltigkeitsregel und koennte veralten, und genau das Veralten ist der Fehler, den diese
 * Umstellung abstellt.
 *
 * ============================ WELCHE SEITE GEWINNT ============================
 *
 * Bei jedem Feld, das BEIDE Seiten fuehren koennten, gewinnt JTL - sonst waere die Umstellung
 * sinnlos. Die Hub-Zeile liefert die uebrigen Felder und, wo vorhanden, die `id`.
 *
 * kLager/lagerName, Bezugsauftrag, interner Kommentar, Liefertermin-Freitext, Waehrungsfaktor,
 * bRowversion (Kopf) sowie Sortierung, Lieferanten-Bezeichnung und Lieferanten-Artikelnummer
 * (Position) haben KEINE Hub-Entsprechung IN DIESEM SINNE: `order_item_extras` fuehrt keine der drei
 * Positionsspalten, und `order_extras` fuehrt weder `bezugsAuftragsNummer`, `internerKommentar`,
 * `faktor` noch eine Rowversion. RICHTIGGESTELLT (Auftrag E): `order_extras.warehouse`/`.notes`
 * EXISTIEREN NICHT mehr (Auftrag D, 2026-08-22, `backend/migrations/017_order_extras_warehouse_
 * notes_entfernen.sql`) - `FELDGRUPPEN_SPALTEN.extras` (`api/bestellungen.ts`) fuehrt beide seither
 * ebenfalls nicht mehr. DASSELBE gilt seit Auftrag "Liefertermin auf echtes JTL-Feld umstellen"
 * (2026-08-26) fuer `order_extras.delivery_term_text` (Migration 024) - der bisherige Hub-eigene
 * "einmal befuellbar, dann gesperrt"-Mechanismus ist vollstaendig entfernt. Seit Auftrag C2b bzw.
 * diesem Auftrag liest/schreibt KEINE Drawer-Komponente mehr auf einer dieser Spalten (siehe
 * Kopfkommentar oben); `kLager`/`lagerName`/`internerKommentar`/`deliveryTermText` aus JTL sind
 * seither der alleinige, in Anzeige UND Schreibweg verwendete Stand.
 * Alle zehn Felder stehen deshalb in `zusammenfuehren`/`alsOrderItem` unten NACH der `...hub`-
 * Spreizung, damit kein Overlay-Wert (auch keiner aus einer aelteren Extras-Zeile) sie je
 * ueberschreiben kann.
 *
 * Bestellungen, die nur in JTL stehen (der Normalfall: von 94 offenen kennt der Hub heute zwei),
 * bekommen eine abgeleitete Kennung `jtl-<Nummer>`. Sie ist stabil - dieselbe Bestellung traegt
 * ueber Seitenaufbauten hinweg dieselbe Kennung -, und sie ist als abgeleitet erkennbar. Sobald
 * jemand an so einer Bestellung etwas tool-eigenes pflegt, entsteht die echte Hub-Zeile und
 * uebernimmt.
 */
import { anfrage } from "./basis.ts";
import {
  abgeleiteteKennung,
  berechneFocVorbelegung,
  istAbgeleiteteKennung,
  jtlBestellungenZusammenfuehren,
  nummerAusAbgeleiteterKennung,
  zusammenfuehren,
} from "../lib/jtlZusammenfuehren.ts";
import type { JtlBestellungRoh, JtlLoeschFelder, JtlPositionRoh } from "../lib/jtlZusammenfuehren.ts";
import type { Order } from "../lib/types.ts";
import type { Archivfilter, BoardStateSelection } from "../state/adresse.ts";
import { schreibeBoardStates } from "../state/adresse.ts";

/**
 * Der reine Rechenteil (Typen der Serverantwort, Kennungshelfer, FOC-Vorbelegung, Zusammenfuehrung
 * von JTL-Zeile und Hub-Zeile) wohnt seit dem KI-Zugang (Stufe 1) in `lib/jtlZusammenfuehren.ts`, weil
 * der Server dieselbe Rechnung fahren muss. Die Namen bleiben hier erreichbar; was unten steht, ist
 * Abruf und Schreibweg.
 */
export {
  abgeleiteteKennung,
  berechneFocVorbelegung,
  istAbgeleiteteKennung,
  jtlBestellungenZusammenfuehren,
  nummerAusAbgeleiteterKennung,
  zusammenfuehren,
};
export type { JtlBestellungRoh, JtlLoeschFelder, JtlPositionRoh };

/**
 * Holt die Bestellungen aus JTL und mischt die Hub-Zeilen dazu.
 *
 * Welche Bestellungen kommen, entscheidet die Route (Sichtbarkeitsregel des Auftraggebers, zuletzt
 * geaendert im Auftrag "Alle JTL-Bestellungen sichtbar machen", 2026-08-27): ALLE nicht in JTL
 * geloeschten Bestellungen, ohne Status- oder Bekanntheitsfilter - vorher nur die in JTL offenen
 * plus alle, die der Hub kannte, was ca. 2000 in JTL abgeschlossene, dem Hub unbekannte
 * Bestellungen unsichtbar machte. Das Frontend filtert hier bewusst NICHT nach - eine zweite Regel
 * an einer zweiten Stelle liefe irgendwann auseinander.
 *
 * `archiv` (Auftrag "Serverseitiger Archivfilter + Ladeanzeige", 2026-08-27): geht als Query-Parameter
 * an die Route, die seither NUR NOCH die zum Archivstatus passende Teilmenge liefert - vorher lud
 * diese Funktion IMMER alle nicht geloeschten Bestellungen, und `BoardSeite.tsx` filterte
 * clientseitig auf `order.archived` herunter. `undefined` heisst: kein Parameter, die Route nimmt
 * dann selbst ihre Vorgabe (`active`) - dieselbe Wahl wie beim `grenze`-Parameter, den diese Funktion
 * ebenfalls nie mitschickt.
 *
 * Hub-Zeilen ohne Gegenstueck in der Antwort erscheinen nicht: entweder ist die Bestellung in JTL
 * geloescht, oder sie liegt jenseits der Listengrenze. Beides ist kein Grund, eine Karteileiche
 * anzuzeigen.
 */
/**
 * `states` (Auftrag 06, "Zustandsmenge statt Archivwert"): die MENGE der gewuenschten Board-Zustaende,
 * als wiederholter `state`-Query-Parameter (`?state=active&state=deleted`, kanonische Reihenfolge) -
 * Kodierung ueber `adresse.ts#schreibeBoardStates`, damit API und Adressgrammatik NICHT auseinander
 * laufen koennen. `undefined` heisst: kein Parameter, die Route nimmt ihre eigene Vorgabe (`active`) -
 * dieselbe Wahl wie beim `grenze`-Parameter, den diese Funktion ebenfalls nie mitschickt.
 *
 * Die leere Menge schreibt `state=none` (Regel 6 des Auftragsvertrags) - nur so unterscheidet sie
 * sich serverseitig von "gar kein Parameter" (Vorgabe).
 *
 * Hub-Zeilen ohne Gegenstueck in der Antwort erscheinen nicht: entweder ist die Bestellung in JTL
 * geloescht, oder sie liegt jenseits der Listengrenze. Beides ist kein Grund, eine Karteileiche
 * anzuzeigen.
 */
export async function jtlBestellungenLaden(
  hubZeilen: readonly Order[],
  states?: BoardStateSelection,
  signal?: AbortSignal,
): Promise<Array<Order & JtlLoeschFelder>> {
  return jtlBestellungenZusammenfuehren(await jtlBestellungenRohLaden(states, signal), hubZeilen);
}

/** Der Abruf allein: `GET /api/jtl/orders` ohne Zusammenfuehrung (siehe `jtlBestellungenLaden`). */
export async function jtlBestellungenRohLaden(
  states?: BoardStateSelection,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh[]> {
  const p = new URLSearchParams();
  if (states !== undefined) schreibeBoardStates(p, states);
  const abfrage = p.toString();
  const pfad = abfrage ? `/api/jtl/orders?${abfrage}` : "/api/jtl/orders";
  const antwort = await anfrage<{ bestellungen: JtlBestellungRoh[] }>(pfad, { signal });
  return antwort.bestellungen;
}


/**
 * EINE Bestellung aus JTL - `GET /api/jtl/orders/:kLieferantenBestellung`
 * (`backend/src/http/routes/jtl.ts:254`). Antwortet mit dem Objekt selbst, NICHT unter einem
 * benannten Feld: die Antwortform des Hauses ist "Mehreres -> benanntes Feld, Einzelnes -> direkt"
 * (siehe dortiger Kommentar an der Listenroute).
 *
 * ================= WOFUER DAS GEBRAUCHT WIRD - und was ohne es kaputt war =======================
 *
 * Bis zum 2026-08-15 gab es zu dieser Route keinen Aufrufer. `jtlBestellungenLaden` oben lief NUR
 * im Anfangsbestand (`state/Speicher.tsx#bestandLaden`); jedes NACHLADEN einer einzelnen Bestellung
 * ging ueber `bestellungLaden` (`api/bestellungen.ts`) und lieferte damit die reine Hub-Zeile.
 * Weil der Store einen Datensatz beim Nachladen VOLLSTAENDIG ersetzt (`reduzierer.ts`), verlor die
 * Bestellung nach JEDEM Schreibvorgang im Drawer alles, was nur aus JTL kommt:
 *
 *   ersatzlos weg   orderNo, waehrung, eur, jtlStatus, jtlStatusLabel, bestaetigt, jtlLieferdatum
 *   still veraendert invoiceNo und orderDate - beide gibt es AUCH als Hub-Spalte (`invoice_no`,
 *                   `order_date` in FELDGRUPPEN_SPALTEN.extras), und der Hub-Wert gewann
 *   entkernt        items - die Hub-Fassung kommt aus `order_item_extras`, und dort gibt es weder
 *                   `qty` noch `price` (001_fundament.sql:156). `orderTotal`/`calcEk` rechneten
 *                   danach mit 0
 *
 * Sichtbar wurde das als: Bestellnummer weg, Bestellwert 0, EK-Tabelle leer - nach dem Speichern
 * eines BELIEBIGEN Feldes. Nicht auf Aufgaben beschraenkt; `drawerAendern` und der Dokumenten-Upload
 * taten seit `60b076e` dasselbe.
 *
 * `kLieferantenBestellung` nimmt seit der Nachbesserung "Serverseitiger Archivfilter + Ladeanzeige"
 * (Befund 1) auch eine STRING-Kennung an - fuer den Reparaturweg in `App.tsx`
 * (`bestellungNachKennungNachladen`), der die eigene Bestellnummer (`orderNo`) unveraendert an die
 * Route durchreicht; die Route selbst versucht dort zuerst die numerische Kennung und faellt bei
 * einem Textwert auf `cEigeneBestellnummer` zurueck (`backend/src/http/routes/jtl.ts`). Fuer jeden
 * bisherigen (numerischen) Aufrufer aendert sich nichts - `String(x)` liefert fuer eine Zahl
 * dasselbe wie zuvor.
 */
export async function jtlBestellungLaden(
  kLieferantenBestellung: number | string,
  signal?: AbortSignal,
  hintergrund?: boolean,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}`,
    { signal, hintergrund },
  );
}

/**
 * Schreibt einen TEIL des JTL-Bestellkopfs - `PATCH /api/jtl/orders/:kLieferantenBestellung`
 * (`backend/src/http/routes/jtlSchreiben.ts`, `backend/src/jtl/schreiben/kopfLesen.ts
 * #aendereBestellkopfTeilweise`). Auftrag C2b: der einzige Schreibweg fuer internen Kommentar
 * (`BestellungTab.tsx`, Feld `cInternerKommentar`) - der ging bis hierhin ueber `order_extras.notes`
 * (Hub-Overlay), OHNE je etwas in JTL zu aendern (siehe Kopfkommentar oben).
 *
 * RICHTIGGESTELLT (Nachtrag "Bestellfelder nach Anlage sperren", Abschnitt 5, 2026-08-25):
 * `Stammdaten.tsx` schrieb das Ziellager (`kLager`) bis dahin ebenfalls ueber diesen Kanal - seither
 * ist das Lager nach der Anlage unbedingt gesperrt, `Stammdaten.tsx` reicht diesen Rueckruf gar
 * nicht mehr durch (`MainTab/typen.ts#onJtlKopfAendern`). Der Server weist `kopf.kLager`
 * ausserdem unbedingt mit 400 ab (`http/routes/jtlSchreiben.ts#alsTeilKopf`), unabhaengig vom
 * Aufrufer.
 *
 * `kopf` traegt NUR die geaenderten Feldnamen der Prozedursignatur (`KOPF_FELDNAMEN`,
 * `jtlSchreiben.ts`) - ein unbekannter Name scheitert dort mit 400, ein bekannter, aber hier nicht
 * gesetzter bleibt auf dem JTL-Stand, den das Backend selbst nachliest (`aendereBestellkopfTeilweise`,
 * "vollstaendig - ... Teilmenge"). `cInternerKommentar` ist der einzige Feldname, den dieser Zweig
 * hier tatsaechlich schickt.
 *
 * `bRowversion` ist die zuletzt GELESENE Version dieser Bestellung (`JtlBestellungRoh.bRowversion`) -
 * fehlt sie (`undefined`), schreibt das Backend OHNE Konfliktpruefung (Rueckwaertskompatibilitaet,
 * siehe dortiger Kommentar); weicht sie vom aktuellen JTL-Stand ab, wirft die Route einen 409
 * (`KonfliktFehler`, `api/basis.ts`), dessen Koerper den aktuellen JTL-Stand traegt.
 *
 * Liefert den VOLLSTAENDIGEN, frisch aus JTL zurueckgelesenen Bestellkopf samt Positionen und neuer
 * `bRowversion` - der Aufrufer (`App.tsx#drawerJtlKopfAendern`) fuehrt ihn ueber `zusammenfuehren` mit
 * der Hub-Zeile zusammen und uebernimmt ihn in den Zustand. OHNE diese Uebernahme scheiterte eine
 * ZWEITE Aenderung derselben Bestellung deterministisch an der veralteten `bRowversion` (409, siehe
 * Kopfkommentar `aendereBestellkopfTeilweise`).
 */
export async function jtlBestellkopfAendern(
  kLieferantenBestellung: number,
  kopf: Record<string, unknown>,
  bRowversion: string | undefined,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}`,
    {
      methode: "PATCH",
      koerper: bRowversion === undefined ? { kopf } : { kopf, bRowversion },
      signal,
    },
  );
}

/**
 * Schreibt kArtikel/cArtNr/cName EINER bestehenden Position - `PATCH /api/jtl/orders/
 * :kLieferantenBestellung/positions/:kLieferantenBestellungPos/artikel`
 * (`backend/src/http/routes/jtlSchreiben.ts`). Der Artikeltausch im Bestell-Reiter
 * (`Drawer/MainTab/Positionen.tsx#uebernehmen`) - siehe docs/superpowers/specs/
 * 2026-09-01-artikeltausch-bestehende-position-design.md.
 *
 * Anders als `jtlBestellkopfAendern` NICHT feldunabhaengig: `aenderung` ist immer das vollstaendige
 * Tripel `{kArtikel, cArtNr, cName}` - der Server uebernimmt Menge/Preis/Steuersatz/Liefertermin
 * unveraendert aus dem frisch gelesenen JTL-Stand, der Aufrufer muss sie nicht kennen.
 *
 * `bRowversion` optional wie bei `jtlBestellkopfAendern`: fehlt sie, schreibt der Server ohne
 * Konfliktpruefung. Liefert den VOLLSTAENDIGEN, frisch aus JTL zurueckgelesenen Bestellkopf samt
 * Positionen und neuer `bRowversion` - derselbe Ruecklese-Vertrag wie beim Kopf-PATCH.
 */
export async function jtlPositionArtikelTauschen(
  kLieferantenBestellung: number,
  kLieferantenBestellungPos: number,
  aenderung: { kArtikel: number; cArtNr: string; cName: string | null },
  bRowversion: string | undefined,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}/positions/` +
      `${encodeURIComponent(String(kLieferantenBestellungPos))}/artikel`,
    {
      methode: "PATCH",
      koerper: bRowversion === undefined ? aenderung : { ...aenderung, bRowversion },
      signal,
    },
  );
}

/**
 * Schreibt Menge/Preis EINER bestehenden Position - `PATCH /api/jtl/orders/
 * :kLieferantenBestellung/positions/:kLieferantenBestellungPos`. Design: docs/superpowers/specs/
 * 2026-09-04-positionen-crud-design.md.
 */
export async function jtlPositionMengePreisAendern(
  kLieferantenBestellung: number,
  kLieferantenBestellungPos: number,
  aenderung: { qty?: number; price?: number },
  bRowversion: string | undefined,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}/positions/` +
      `${encodeURIComponent(String(kLieferantenBestellungPos))}`,
    {
      methode: "PATCH",
      koerper: bRowversion === undefined ? aenderung : { ...aenderung, bRowversion },
      signal,
    },
  );
}

/**
 * Legt eine NEUE Position an einer bestehenden Bestellung an - `POST /api/jtl/orders/
 * :kLieferantenBestellung/positions`. Design: docs/superpowers/specs/
 * 2026-09-04-positionen-crud-design.md.
 */
export async function jtlPositionHinzufuegen(
  kLieferantenBestellung: number,
  neuePosition: {
    kArtikel: number;
    cArtNr: string;
    cName: string;
    qty: number;
    price: number;
    mwstProzent: number;
  },
  bRowversion: string | undefined,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}/positions`,
    {
      methode: "POST",
      koerper: bRowversion === undefined ? neuePosition : { ...neuePosition, bRowversion },
      signal,
    },
  );
}

/**
 * Loescht EINE bestehende Position - `DELETE /api/jtl/orders/:kLieferantenBestellung/positions/
 * :kLieferantenBestellungPos`. Design: docs/superpowers/specs/2026-09-04-positionen-crud-design.md.
 */
export async function jtlPositionLoeschen(
  kLieferantenBestellung: number,
  kLieferantenBestellungPos: number,
  bRowversion: string | undefined,
  signal?: AbortSignal,
): Promise<JtlBestellungRoh> {
  return await anfrage<JtlBestellungRoh>(
    `/api/jtl/orders/${encodeURIComponent(String(kLieferantenBestellung))}/positions/` +
      `${encodeURIComponent(String(kLieferantenBestellungPos))}`,
    {
      methode: "DELETE",
      koerper: bRowversion === undefined ? {} : { bRowversion },
      signal,
    },
  );
}

/**
 * Dreiwertig statt boolesch (Fixwelle nach der finalen Gesamtpruefung, 2026-08-26, Punkt 2) -
 * spiegelt `LoeschErgebnis` im Backend (`backend/src/jtl/schreiben/loeschenOrchestrierung.ts`)
 * woertlich. `"nicht_noetig"` (keine Hub-Zeile bzw. kein Ordner-Aufraeumschritt vorgesehen) und
 * `"entfernt"` sind kein Fehler; nur `"fehlgeschlagen"` (der Aufraeumschritt wurde versucht, ist
 * aber gescheitert) loest im Aufrufer (`App.tsx#jtlLoeschenBestaetigt`) einen Zusatzhinweis aus.
 */
export type JtlLoeschAufraeumStatus = "nicht_noetig" | "entfernt" | "fehlgeschlagen";

/**
 * Loescht eine EK-Bestellung SAMT JTL - `DELETE /api/jtl/orders/:kLieferantenBestellung` (Backend
 * Task 1-9, bereits fertig). `grund` ist der Freitext aus dem Bestaetigungsdialog (Task 11), den das
 * Backend protokolliert. Liefert das Antwortobjekt UNVERAENDERT durch, keine Umformung: der
 * Aufrufer (Task 12, seit der Fixwelle vom 2026-08-26 auch bei `"fehlgeschlagen"`) entscheidet
 * selbst, was `hubZeileStatus`/`ordnerStatus` fuer ihn bedeuten.
 */
export function jtlBestellungLoeschen(
  kLieferantenBestellung: number,
  grund: string,
): Promise<{ ok: true; hubZeileStatus: JtlLoeschAufraeumStatus; ordnerStatus: JtlLoeschAufraeumStatus }> {
  return anfrage(`/api/jtl/orders/${kLieferantenBestellung}`, {
    methode: "DELETE",
    koerper: { grund },
  });
}
